import type { Command } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { Transaction, EditorState } from '@tiptap/pm/state';
import {
  getActiveBlock,
  findParentRow,
  isLastChildOf,
  findFirstNonEmptyAfter,
  insertAtEndOfRow,
  selectAtBlockStart,
  getBlockAlign,
} from './rowUtils';
import { ELEMENT_ROW_NODE_NAME } from '../constants';

/**
 * Merge the *following* row (srcRowPos) into the *current* row (destRowPos).
 * Strategy:
 *  1) Read both rows from the current tr.doc (no mapping on entry).
 *  2) Build a single row with children: [destRow.content..., srcRow.content...].
 *  3) Replace the full span [destRowPos, srcRowPos + srcRow.nodeSize) with the new row.
 *  4) Return the absolute position of the first child that originally came from the src row.
 *     (Callers use this to place the caret.)
 */
function mergeFollowingRowInto(tr: Transaction, destRowPos: number, srcRowPos: number) {
  const destRow = tr.doc.nodeAt(destRowPos);
  const srcRow = tr.doc.nodeAt(srcRowPos);
  if (!destRow || !srcRow || destRow.type !== srcRow.type) {
    return { tr, firstMergedPos: null as number | null };
  }

  // Gather children (dest first, then src)
  const mergedChildren: PMNode[] = [];
  destRow.content.forEach((n) => mergedChildren.push(n));
  srcRow.content.forEach((n) => mergedChildren.push(n));

  // Build replacement row keeping destRow attrs (preserves alignment)
  const rowType = destRow.type;
  let replacement: PMNode;
  try {
    replacement = rowType.create({ ...destRow.attrs }, mergedChildren);
  } catch {
    return { tr, firstMergedPos: null as number | null };
  }

  // Replace both rows with the merged row
  const from = destRowPos;
  const to = srcRowPos + srcRow.nodeSize;
  tr = tr.replaceRangeWith(from, to, replacement);

  // First merged child starts after all original dest children inside the new row
  const newRowPos = tr.mapping.map(from);
  let offsetInside = 1; // inner content starts at +1
  destRow.content.forEach((n) => (offsetInside += n.nodeSize));
  const firstMergedPos = newRowPos + offsetInside;

  return { tr, firstMergedPos };
}

/**
 * MS Word–like wrap:
 * - If selection is *inside a row* and on the last child, pull the first non-empty block below
 *   into the row; if that block is a row, merge rows (keep current row's alignment).
 * - If *not in a row*, create a new row around the selected block (alignment inferred from it),
 *   then either pull the first non-empty block below into the row or merge with that row.
 * - Caret is moved onto the element that was just pulled/merged in.
 */
export function wrapLikeWord(): Command {
  return ({ state, dispatch }) => {
    if (!dispatch) return false;

    const rowType = state.schema.nodes[ELEMENT_ROW_NODE_NAME];
    const info = getActiveBlock(state);
    if (!info) return false;

    let tr = state.tr;
    const { pos, node } = info;

    // ---------- Case B: selection inside a row ----------
    const rowInfo = findParentRow(state, pos);
    if (rowInfo) {
      // Only last child can “wrap” further content in
      if (!isLastChildOf(rowInfo.node, pos, rowInfo.pos)) return false;

      const afterRow = rowInfo.pos + rowInfo.node.nodeSize;
      const nextReal = findFirstNonEmptyAfter({ ...state, doc: tr.doc } as EditorState, afterRow);
      if (!nextReal) return false;

      const { node: next, pos: nextPos } = nextReal;

      // If the next block is also a row → merge rows (keep current row's attrs/alignment)
      if (next.type === rowType) {
        const { tr: tr2, firstMergedPos } = mergeFollowingRowInto(tr, rowInfo.pos, nextPos);
        if (firstMergedPos == null) return false;
        tr = selectAtBlockStart(tr2, firstMergedPos);
        dispatch(tr.scrollIntoView());
        return true;
      }

      // Otherwise pull that block into the end of the current row
      tr = tr.delete(nextPos, nextPos + next.nodeSize);
      const mappedRowPos = tr.mapping.map(rowInfo.pos);
      const { tr: tr3, insertedPos } = insertAtEndOfRow(tr, mappedRowPos, next.copy(next.content));
      if (insertedPos == null) return false;

      tr = selectAtBlockStart(tr3, insertedPos);
      dispatch(tr.scrollIntoView());
      return true;
    }

    // ---------- Case A: selection not in a row ----------
    // A1: Create a row around the selected block (infer alignment from the block)
    const align = getBlockAlign(node);
    const row = rowType.create({ textAlign: align }, node.copy(node.content));
    tr = tr.replaceRangeWith(pos, pos + node.nodeSize, row);

    // A2: Look for the first real block after the new row (can be a row)
    const mappedRowPos = tr.mapping.map(pos);
    const mappedRowNode = tr.doc.nodeAt(mappedRowPos);
    const afterRow = mappedRowPos + (mappedRowNode?.nodeSize ?? 0);

    const nextReal = findFirstNonEmptyAfter({ ...state, doc: tr.doc } as EditorState, afterRow);
    if (!nextReal) {
      // Nothing to pull - keep caret on the sole child of the row
      tr = selectAtBlockStart(tr, mappedRowPos + 1);
      dispatch(tr.scrollIntoView());
      return true;
    }

    const { node: next, pos: nextPos } = nextReal;

    // If the next block is a row → merge it into the new row
    if (next.type === rowType) {
      const { tr: tr2, firstMergedPos } = mergeFollowingRowInto(tr, mappedRowPos, nextPos);
      if (firstMergedPos == null) return false;
      tr = selectAtBlockStart(tr2, firstMergedPos);
      dispatch(tr.scrollIntoView());
      return true;
    }

    // Otherwise, pull that block into the row
    tr = tr.delete(nextPos, nextPos + next.nodeSize);
    const mappedRowPos2 = tr.mapping.map(mappedRowPos);
    const { tr: tr3, insertedPos } = insertAtEndOfRow(tr, mappedRowPos2, next.copy(next.content));
    if (insertedPos == null) return false;

    tr = selectAtBlockStart(tr3, insertedPos);
    dispatch(tr.scrollIntoView());
    return true;
  };
}
